import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { ChecklistRule } from '../../../validation-checklist';
import { FormGroup } from '@angular/forms';

@Component({
    selector: 'ft-pending-changes',
    templateUrl: './pending-changes.component.html',
    styleUrls: ['./pending-changes.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PendingChangesComponent implements OnChanges {
    @Input() title: string;
    @Input() titleVisible = false;
    @Input() message: string;
    @Input() totalChanges: number;
    @Input() hasChanges: boolean;
    @Input() hasDraftChanges: boolean;
    @Input() showCounter = true;
    @Input() draftSaveButtonLabel = 'Save draft';
    @Input() alwaysVisible = false;
    @Input() cancelVisible = true;
    @Input() publishVisible = true;
    @Input() draftSaveVisible = false;
    @Input() set loading$(value: Observable<boolean> | Array<Observable<boolean>> | boolean) {
        if (Array.isArray(value)) {
            this.isLoading$ = combineLatest(value).pipe(map(values => values.some(v => v)));
        } else if (value instanceof Observable) {
            this.isLoading$ = value;
        } else {
            this.isLoading$ = of(!!value);
        }
    }
    isLoading$: Observable<boolean> = of(false);
    @Input() canCommit = true;
    @Input() canCommitDraft = true;
    @Input() disabled = false;
    @Input() validationRules: ChecklistRule[] = [];
    @Input() validationRulesAlwaysVisible = false;
    @Input() cancelConfirmation = false;

    @Input() activeFormGroup: FormGroup;

    @Input() isSticky = true;
    @Output() commit = new EventEmitter();
    @Output() draftCommit = new EventEmitter();
    @Output() cancel = new EventEmitter();
    // Seems like the `ft-validation-checklist` component calls to ngDoCheck are preventing the two-way binding from updating the parent component properly
    // By using BehaviorSubject and Observable we ensure that changes to isValid are managed reactively,
    // and the parent component can easily track and respond to these changes through the BehaviorSubject and Observable mechanisms.
    // This approach prevents infinite loops and ensures efficient state management between components.
    canSaveSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
        this.getCanSave()
    );
    canSaveDraftSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
        this.getCanSaveDraft()
    );
    canSave$: Observable<boolean> = this.canSaveSubject$.asObservable().pipe(delay(0));
    canSaveDraft$: Observable<boolean> = this.canSaveDraftSubject$.asObservable().pipe(delay(0));

    constructor() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.disabled || changes.canCommit) {
            this.updateCanSave();
        }

        if (changes.disabled || changes.canCommitDraft) {
            this.updateCanSaveDraft();
        }
    }

    onIsValidChange() {
        this.updateCanSave();
    }

    updateCanSave() {
        this.canSaveSubject$.next(this.getCanSave());
    }

    updateCanSaveDraft() {
        this.canSaveDraftSubject$.next(this.getCanSaveDraft());
    }

    getCanSave() {
        return !this.disabled && this.canCommit;
    }

    getCanSaveDraft() {
        return !this.disabled && this.canCommitDraft;
    }
}
